































































































































































































































import { BModal } from 'bootstrap-vue';
import { SimulatorState } from '@/simulator';
import { TDictionary, asciiTable } from "@/assets";
import Vue from 'vue';
import { highlight, languages } from 'prismjs';
import { TInstructionNode } from '@/syntax/types';
import debug from './debug.vue';
import { TAllocation, TDeclaration } from '@/simulator/types';
import clone from "lodash.clonedeep";
import Shepherd from 'shepherd.js';
import { dataTypeByteSizeMap, dataTypeMap } from '@/constants';

type TTip = {
	title: string;
	detail: string;
}

type TBoundary = {
	left?: number;
	right?: number;
	top: number[];
	bottom: number[];
}

export default Vue.extend({
	name: 'explorer',
	components: {
		debug
	},
	data() {
		return {
			section: undefined as string | undefined,

			size: 'byte',
			sizes: [
				{ text: 'byte', value: 'byte' },
				{ text: 'word', value: 'word' }
			],

			datatype: 'hex',
			datatypes: [
				{ text: 'hex', value: 'hex' },
				{ text: 'dec', value: 'dec' },
				{ text: 'bin', value: 'bin' }
			],

			endianness: 'little',
			endiannesses: [
				{ text: 'little', value: 'little' },
				{ text: 'big', value: 'big' }
			],

			instructionIndex: undefined as number | undefined,
			highlitData: [] as number[],
			hoveredDeclaration: undefined as TDeclaration | undefined,

			tooltip: {} as TTip,
			tips: {
				default: {
					title: "memory explorer",
					detail: // html
					`\
						This explorer displays the current contents of the simulated memory arranged vertically into words and horizontally\
						into the 4 bytes which make up each word.

						Initialised regions are divided into <span class="label-text">text</span>, <span class="label-data">data</span> and\
						<span class="label-stack">stack</span>. The unfenced region between the <span class="label-data">data</span> and\
						<span class="label-stack">stack</span> is unintialised memory.
					`
				},
				text: {
					title: `<span class="label-text">text</span>`,
					detail: // html
					`\
						This region, situated in the lowest addresses of the program's virtual address space, contains the machine code\
						for each instruction in the program.
						
						In an ARMv7 CPU, every instruction fits into one word, so each row of the explorer within the text section is\
						a single instruction from the loaded program, in the order that they were assembled.
					`
				},
				data: {
					title: `<span class="label-data">data</span>`,
					detail: // html
					`\
						This region, which immediately follows the <span class="label-text">text</span> region, contains all static data\
						defined in the .data region of the loaded program. It is used for storing information which cannot be represented\
						in assembly language immediate values - such as strings and arrays. 

						If you have defined an <span class="token directive">.asciz</span> string in the <span class="token directive">.data</span>\
						region of your program, you can switch to the ascii datatype and read the string as it appears: byte by byte in memory.

						This area of the virtual address space may be freely modified by the associated program; however, it cannot be extended\
						i.e. the data can be changed, but not added to.
					`
				},
				heap: {
					title: `<span class="label-heap">heap</span>`,
					detail: // html
					`\
						This region is the <span class="label-heap">heap</span> and contains data which has been dynamically allocated during the execution of your program.
					`
				},
				uninitialised: {
					title: "uninitialised memory",
					detail: // html
					`\
						This region contains uninitialised memory, separating the heap (an area of dynamic memory allocation which immediately\
						follows the data section) from the stack in the virtual address space allocated to the loaded program.

						The bytes in this section cannot be assumed to be clean, they may contain junk data from whatever process owned this\
						physical address space before it was allocated to your program. Be aware of this when allocating dynamic data to\
						the heap.
					`
				},
				stack: {
					title: `<span class="label-stack">stack</span>`,
					detail: // html
					`\
						The stack is located in the highest addressess of the owning program's virtual address space. As items are pushed onto\
						the stack, it grows downwards through the addresses until it meets the top of the heap, at which point a segmentation fault\
						is thrown.

						According to the Procedure Call Standard for the ARM Architecture (AAPCS), the stack should be 8-byte aligned, this means\
						that the stack must always contain an even number of words. There's nothing forcing you to do this but it is considered\
						good practice because it improves memory access speeds on some systems. 
					`
				},
				endianness: {
					title: "endianness",
					detail: // html
					`\
						'Endianness' describes the byte order within collections of bytes in memory. Since ARMv7 is a 32 bit processor,\
						the most common 'collection' of bytes are 32-bit (4-byte) integers. In almost all modern computers, numbers are\
						stored '<span class="irisc">little-endian</span>', meaning that the 'logical' byte order as a human might expect\
						is reversed (!!).

						To take a common example, the hexadecimal number <span class="token immediate">0x12345678</span> is split into its\
						respective bytes:\

						<div class="ml-3">
							<span class="label-stack">0x12</span>\
							<span class="label-text">0x34</span>\
							<span class="label-data">0x56</span>\
							<span class="token immediate">0x78</span>
						</div>\

						In '<span class="irisc">big-endian</span>', these bytes are stored in the order shown; however, in\
						'<span class="irisc">little-endian</span>', the order of the bytes is reversed:\
						
						<div class="ml-3">
							<span class="token immediate">0x78</span>\
							<span class="label-data">0x56</span>\
							<span class="label-text">0x34</span>\
							<span class="label-stack">0x12</span>
						</div>\

						This reversed order is what is actually contained within the memory. I have provided a way to switch the endianness\
						of each word so that they make a bit more sense when read as a whole.
					`
				},
			} as TDictionary<TTip>,

			controlTooltip: undefined as string | undefined,

			ascii: asciiTable.ascii,
			dataTypeMap: dataTypeMap,
			dataTypeByteSizeMap: dataTypeByteSizeMap,
			shown: false
		};
	},

	computed: {
		byteView: SimulatorState.byteView,
		wordView: SimulatorState.wordView,
		memSize: () => SimulatorState.memory().size,

		memIsInitialised: () => SimulatorState.memory().buffer !== undefined,

		textHeight: () => SimulatorState.memory().textHeight,
		dataHeight: () => SimulatorState.memory().dataHeight,
		heapHeight: () => SimulatorState.memory().heapHeight,
		stackHeight: () => SimulatorState.memory().stackHeight,

		textWordHeight: function (): number { return this.textHeight / 4 },
		dataWordHeight: function (): number { return this.dataHeight / 4 },
		heapWordHeight: function (): number { return this.heapHeight / 4 },
		stackWordHeight: function (): number { return this.stackHeight / 4 },
		uninitWordHeight: function (): number { return Math.max((this.memSize / 4) - this.textWordHeight - this.dataWordHeight  - this.heapWordHeight - this.stackWordHeight, 0) },

		textOffset: function (): number { return 0; },
		dataOffset: function (): number { return this.textOffset + this.textWordHeight; },
		heapOffset: function (): number { return this.dataOffset + this.dataWordHeight; },
		uninitOffset: function (): number { return this.heapOffset + this.heapWordHeight; },
		stackOffset: function (): number { return this.uninitOffset + this.uninitWordHeight; },

		dataMap: () => SimulatorState.memory().dataMap,
		dataDeclarations: function (): TDeclaration[] {
			return Array.from(this.dataMap)
				.sort(([aPtr, _a], [bPtr, _b]) => {
					return aPtr - bPtr;
				})
				.map(e => e[1]);
		},

		heapMap: () => SimulatorState.memory().heapMap,
		heapBlocks: function (): TAllocation[] {
			return Array.from(this.heapMap)
				.sort(([aPtr, _a], [bPtr, _b]) => {
					return aPtr - bPtr;
				})
				.map(e => e[1]);
		},
		heapContiguousBlocks: function (): TAllocation[] {
			return this.heapBlocks
				.reduce((contig, block, index, allocations) => {
					const lastAllocation = allocations[index - 1];

					if (lastAllocation?.allocated !== block.allocated) {
						contig.push(clone(block));
					}
					else contig[contig.length - 1].size += block.size;

					return contig;
				}, [] as TAllocation[])
		},



		instructions: () => SimulatorState.memory().text,
		instruction: function (): TInstructionNode | undefined {
			if (this.instructionIndex !== undefined) return this.instructions[this.instructionIndex];
			return undefined;
		}
	},

	methods: {
		show: function (section: string) {
			this.shown = false;
			(this.$refs.modal as BModal).show();

			const v = this;
			setTimeout(() => {
				v.shown = true;
			}, 350);
		},

		byteValue: function (wordIndex: number, byteIndex: number): number {
			const index = (wordIndex * 4) + this.endianify(byteIndex);
			return this.byteView[index];
		},

		wordValue: function (wordIndex: number): number {
			return this.wordView[wordIndex];
		},

		endianify(byteIndex: number): number {
			if (this.endianness === 'big') return [3, 2, 1, 0][byteIndex];
			return byteIndex;
		},

		printByte: function (value: number): string {
			if (this.datatype === 'dec') return value.toString(10);
			if (this.datatype === 'bin') return this.binstr(value);
			if (this.datatype === 'ascii') return this.ascii[value][3];
			else return this.hexstr(value);
		},

		printWord: function (value: number): string {
			if (this.datatype === 'dec') return value.toString(10);
			if (this.datatype === 'bin') return this.binstr(value, 32);
			else return this.hexstr(value, 8);
		},

		hexstr: function (value: number, pad: number = 2): string {
			return `${value?.toString(16).padStart(pad, '0')}`
		},

		binstr: function (value: number, pad: number = 8): string {
			return `${value?.toString(2).padStart(pad, '0')}`
		},

		dataHover(wordIndex: number, byteIndex: number) {
			const offset = (wordIndex * 4) + byteIndex;
			const declaration = this.dataDeclarations.find(e => e.offset <= offset && offset <= e.offset + e.size);

			if (declaration) {
				this.highlitData = Array.from(Array(declaration.offset + declaration.size).keys()).slice(declaration.offset);
				this.hoveredDeclaration = declaration;
			}
		},

		highlight: function (instruction: TInstructionNode): string {
			return highlight(instruction.text, languages.armv7, 'ARMv7');
		},

		tip: function (name: string) {
			this.tooltip = this.tips[name];
		},

		untip: function () {
			this.tooltip = this.tips['default'];
		}
	},

	created: function () {
		this.untip();
	},

	watch: {
		size: {
			handler: function (value) {
				if (value === 'word') {
					if (this.datatype === 'ascii') this.datatype = 'hex';
				}
			}
		}
	}
});
